import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Styled from '../configuration-style';
import InfoModal from './info-modal';

/**
 * OptionFolder component to render an option folder structure
 * @param {object} props - The props object
 * @param {object} props.parentNode - The parent node data
 * @param {string} props.parentName - Name of the parent field
 * @param {object} props.node - Current node data
 * @param {string} props.name - Name of the current field
 * @param {Function} props.setIsExpanded - Function to set the expanded state
 * @param {boolean} props.isExpanded - State to check if the folder is expanded
 * @param {number} props.level - Depth level of the folder
 * @param {Function} props.renderChildren - Function to render children nodes
 * @param {Function} props.validateOption - Function to validate an option
 * @param {Function} props.isRequired - Function to check if the field is required
 * @param {object} props.infoModal - State for the info modal
 * @param {Function} props.setInfoModal - Function to set the info modal state
 * @param {string} props.partNumber - Option part number
 * @param {Function} props.handleOptionClick - handle option click
 * @param {Function} props.setExpandedParentOption - set expanded parent option
 * @returns {JSX.Element} The rendered OptionFolder component
 */
const OptionFolder = ({
  parentNode,
  parentName,
  node,
  name,
  setIsExpanded,
  isExpanded,
  level,
  renderChildren,
  validateOption,
  isRequired,
  infoModal,
  handleOptionClick,
  setInfoModal,
  partNumber,
  setExpandedParentOption,
}) => {
  const { control, register } = useFormContext();

  useEffect(() => {
    register(`${name}.node`, {
      value: node.id,
    });
    register(`${name}.single`, {
      value: parentNode.select_type === 'multi',
    });
    register(`${name}.name`, {
      value: node.name,
    });
    register(`${name}.partNumber`, {
      value: partNumber,
    });
    register(`${name}.type`, {
      value: 'option-folder',
    });
  }, [register, parentNode.select_type, name, node.id, node.name, partNumber]);

  return (
    <>
      <Controller
        shouldUnregister={false}
        rules={{
          validate: () =>
            validateOption({
              node,
              parentNode,
              parentName,
              name,
            }),
        }}
        render={({ field: { value, onChange } }) => (
          <>
            <Styled.OptionFolder
              id={`${node.id}_${node.part_number}`}
              data-name={name}
              data-selectType={parentNode.select_type}
              data-required={parentNode.is_required}
              data-deps={node?.dependencies?.length > 0}
              level={level * 20}
              isSelected={value}
              onClick={() => {
                setIsExpanded(!isExpanded);
                setExpandedParentOption();
              }}
            >
              <Styled.ThumbnailOptionFolderContainer>
                {(node?.option_price_link_thumbnail || node.thumbnail) && (
                  <Styled.OptImg
                    src={node?.option_price_link_thumbnail || node.thumbnail}
                  />
                )}
              </Styled.ThumbnailOptionFolderContainer>
              <Styled.OptionFolderNameContainer>
                <Styled.OptionName>
                  {node.name}
                  {isRequired()}
                </Styled.OptionName>
              </Styled.OptionFolderNameContainer>
              <Styled.ActionHolderOptionFolder>
                <Styled.InfoIconOptionFolder
                  relative
                  size={22}
                  onClick={(e) => {
                    e.stopPropagation();
                    setInfoModal({ node, show: true });
                  }}
                />
                <Styled.RadioDisplay
                  $selected={value}
                  onClick={(e) => {
                    if (value === true || isExpanded) {
                      e.stopPropagation();
                    }
                    handleOptionClick({ onChange, value });
                  }}
                />
              </Styled.ActionHolderOptionFolder>
            </Styled.OptionFolder>
            <InfoModal
              id={infoModal.node?.option_price_link_id}
              open={infoModal.show}
              close={() => setInfoModal({ show: false, node: null })}
              addToSelection={() => {
                handleOptionClick({ onChange, value });
                setInfoModal({ node: null, show: false });
              }}
            />
          </>
        )}
        name={`${name}.id`}
        control={control}
      />
      {renderChildren(`${name}.id`, true)}
    </>
  );
};

OptionFolder.propTypes = {
  parentNode: PropTypes.shape({
    id: PropTypes.string,
    select_type: PropTypes.string,
    is_required: PropTypes.bool,
  }).isRequired,
  parentName: PropTypes.string.isRequired,
  node: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    dependencies: PropTypes.array,
    part_number: PropTypes.string,
    is_required: PropTypes.bool,
    thumbnail: PropTypes.string,
    option_price_link_thumbnail: PropTypes.string,
  }).isRequired,
  name: PropTypes.string.isRequired,
  setIsExpanded: PropTypes.func.isRequired,
  handleOptionClick: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  level: PropTypes.number.isRequired,
  renderChildren: PropTypes.func.isRequired,
  validateOption: PropTypes.func.isRequired,
  isRequired: PropTypes.func.isRequired,
  infoModal: PropTypes.shape({
    show: PropTypes.bool,
    node: PropTypes.shape({
      option_price_link_id: PropTypes.string,
    }),
  }).isRequired,
  setInfoModal: PropTypes.func.isRequired,
  partNumber: PropTypes.string.isRequired,
  setExpandedParentOption: PropTypes.func,
};

OptionFolder.defaultProps = {
  setExpandedParentOption: () => {},
};

export default OptionFolder;
